// Small pagination control used by EntriesTab (server-side) and
// DashboardTab's activity table (client-side). Stateless — caller owns
// page / pageSize state.
//
// Renders nothing when there's only one page; otherwise:
//   [<<] [<] Page X of Y [>] [>>]   Showing N-M of T   [25 v] per page
//
// `onPageSizeChange` is optional; omit it to hide the size selector.

const DEFAULT_PAGE_SIZES = [10, 25, 50, 100]

export default function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  className = '',
}) {
  if (typeof total !== 'number' || total < 0) return null
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  if (totalPages <= 1 && total <= pageSize && !onPageSizeChange) return null

  const safePage = Math.max(1, Math.min(page, totalPages))
  const fromIdx = total === 0 ? 0 : (safePage - 1) * pageSize + 1
  const toIdx = Math.min(safePage * pageSize, total)

  const goto = (p) => {
    if (p < 1 || p > totalPages || p === safePage) return
    onPageChange(p)
  }

  const btn = 'px-2.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded ' +
    'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 ' +
    'hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 ' +
    'disabled:cursor-not-allowed transition'

  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 ${className}`}>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => goto(1)}
          disabled={safePage === 1}
          title="First page"
          className={btn}
        >
          «
        </button>
        <button
          type="button"
          onClick={() => goto(safePage - 1)}
          disabled={safePage === 1}
          title="Previous page"
          className={btn}
        >
          ‹
        </button>
        <span className="px-2 text-xs text-gray-700 dark:text-gray-300 tabular-nums">
          Page <strong>{safePage}</strong> of <strong>{totalPages}</strong>
        </span>
        <button
          type="button"
          onClick={() => goto(safePage + 1)}
          disabled={safePage >= totalPages}
          title="Next page"
          className={btn}
        >
          ›
        </button>
        <button
          type="button"
          onClick={() => goto(totalPages)}
          disabled={safePage >= totalPages}
          title="Last page"
          className={btn}
        >
          »
        </button>
      </div>

      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
        <span className="tabular-nums">
          Showing {fromIdx}–{toIdx} of {total}
        </span>
        {onPageSizeChange && (
          <label className="flex items-center gap-2">
            <span className="text-gray-500 dark:text-gray-400">Per page</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-primary-500"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        )}
      </div>
    </div>
  )
}
